/**
 * Field Splat Viewer
 *
 * Loads `public/splats/scene.ply` — the full Gaussian Splat capture
 * of the SRAC field — into @mkkellogg/gaussian-splats-3d's Viewer
 * and places the camera inside the cloud so the visitor lands in
 * the middle of the field on first paint instead of orbiting it
 * from outside.
 *
 * The OrbitControls target is set a few units forward of the camera
 * (along the splat's +X) so dragging rotates the view around a
 * point just in front — feels like turning your head from a fixed
 * spot rather than orbiting the whole scene.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

'use client';

import * as React from 'react';
import {
  Loader2,
  RotateCcw,
  Maximize2,
  Minimize2,
  AlertTriangle,
  Copy,
  Check,
  Pin,
} from 'lucide-react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { useFirestore } from '@/firebase';
import { useUserData } from '@/hooks/use-user-data';
import { useToast } from '@/hooks/use-toast';

interface CameraPose {
  position: [number, number, number];
  target: [number, number, number];
}

/**
 * Hard-coded fallback used when the Firestore-backed pose hasn't
 * been saved yet — captured by hand from a "good first view" of the
 * splat. Admins / officers can overwrite this from the viewer's
 * "Save as default" button without a redeploy.
 */
const FALLBACK_POSE: CameraPose = {
  position: [0.669, -0.254, 0.206],
  target: [-0.188, -0.046, -0.355],
};

interface Props {
  /** PLY (Gaussian Splat) URL — defaults to the field capture. */
  splatSrc?: string;
}

export function FieldSplatViewer({
  splatSrc = '/splats/scene.ply',
}: Props) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const handlesRef = React.useRef<{
    reset: () => void;
    snapshot: () => { position: [number, number, number]; target: [number, number, number] } | null;
    applyPose: (pose: CameraPose) => void;
  } | null>(null);

  const firestore = useFirestore();
  const { isOfficer, isAdmin } = useUserData();
  const canSetDefault = !!isOfficer || !!isAdmin;
  const { toast } = useToast();

  // Latest saved pose from Firestore. Updates live via onSnapshot, so
  // an officer can save a new default in one tab and a Reset in
  // another tab will snap to it.
  const [savedPose, setSavedPose] = React.useState<CameraPose | null>(null);
  const savedPoseRef = React.useRef<CameraPose | null>(null);
  React.useEffect(() => {
    savedPoseRef.current = savedPose;
  }, [savedPose]);

  React.useEffect(() => {
    if (!firestore) return;
    const ref = doc(firestore, 'siteConfig', 'fieldMap');
    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setSavedPose(null);
          return;
        }
        const data = snap.data() as any;
        const p = data?.position;
        const t = data?.target;
        if (
          Array.isArray(p) && p.length === 3 && p.every((n) => typeof n === 'number') &&
          Array.isArray(t) && t.length === 3 && t.every((n) => typeof n === 'number')
        ) {
          setSavedPose({
            position: [p[0], p[1], p[2]],
            target: [t[0], t[1], t[2]],
          });
        }
      },
      (err) => {
        console.warn('[FieldSplatViewer] siteConfig/fieldMap subscribe failed:', err);
      },
    );
  }, [firestore]);

  const [copied, setCopied] = React.useState(false);
  const [savingDefault, setSavingDefault] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [progressLabel, setProgressLabel] = React.useState(
    'Streaming Gaussian Splat…',
  );
  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [fullscreen, setFullscreen] = React.useState(false);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let cleanup: (() => void) | null = null;
    setReady(false);
    setError(null);
    setProgress(0);

    (async () => {
      try {
        const GS3D = await import('@mkkellogg/gaussian-splats-3d');
        const THREE = await import('three');
        if (disposed) return;

        // SharedArrayBuffer requires `crossOriginIsolated`, which the
        // Next dev server / most prod hosts don't enable. Without it
        // the splat sort worker hangs forever if either of these is
        // on, so they get gated together.
        const sabAvailable =
          typeof SharedArrayBuffer !== 'undefined' &&
          (self as any).crossOriginIsolated === true;

        const viewer = new (GS3D as any).Viewer({
          rootElement: container,
          cameraUp: [0, 0, 1],
          sphericalHarmonicsDegree: 2,
          sharedMemoryForWorkers: sabAvailable,
          enableSIMDInSort: sabAvailable,
          useBuiltInControls: true,
          ignoreDevicePixelRatio: false,
        });

        await viewer.addSplatScene(splatSrc, {
          format: (GS3D as any).SceneFormat.Ply,
          // Suppress the library's own spinner / progress bar — our
          // outer overlay (the red bar) is the only loading UI we
          // want visible.
          showLoadingUI: false,
          progressiveLoad: false,
          onProgress: (percent: number) => {
            const p = Math.max(0, Math.min(100, percent));
            setProgress(p);
            if (p >= 99.5) {
              setProgressLabel('Sorting splats…');
            } else {
              setProgressLabel('Streaming Gaussian Splat…');
            }
          },
        });
        if (disposed) return;
        viewer.start();

        // The lib renders a "focus marker" (three big white cones at
        // the orbit target) by default. It dwarfs the splats once
        // the camera is inside the cloud and the target is right in
        // front of you, so silence it. Setting the render-side
        // opacity to 0 short-circuits the marker render call —
        // cheaper than fighting with visibility flags.
        try {
          viewer.sceneHelper?.setFocusMarkerOpacity?.(0);
        } catch {
          /* lib internals shift between minor versions */
        }

        // ── Compute centre of mass + a forward direction inside the
        //    cloud by sampling splat centres. We then drop the camera
        //    at the COM and aim it at COM + forward, so the user
        //    starts from the heart of the field looking out.
        const splatMesh = viewer.splatMesh ?? viewer.getSplatMesh?.();
        const com = new THREE.Vector3();
        let radius = 50;
        if (splatMesh && typeof splatMesh.getSplatCount === 'function') {
          const total = splatMesh.getSplatCount();
          if (total > 0) {
            const sample = Math.min(total, 50_000);
            const stride = Math.max(1, Math.floor(total / sample));
            const tmp = new THREE.Vector3();
            let n = 0;
            for (let i = 0; i < total; i += stride) {
              splatMesh.getSplatCenter(i, tmp);
              com.add(tmp);
              n += 1;
            }
            if (n > 0) com.divideScalar(n);
            // Second pass — find max distance from COM for near/far
            // plane sizing.
            let maxD = 0;
            for (let i = 0; i < total; i += stride) {
              splatMesh.getSplatCenter(i, tmp);
              const d = tmp.distanceTo(com);
              if (d > maxD) maxD = d;
            }
            radius = Math.max(maxD, 1);
          }
        }

        // Initial camera placement priority:
        //  1. Pose saved in Firestore (siteConfig/fieldMap) — admins
        //     overwrite this whenever they find a better viewpoint.
        //  2. Hard-coded FALLBACK_POSE — a captured "good" view kept
        //     in source so the page is reasonable on first paint
        //     even before anyone saves.
        //  3. COM-derived heuristic — camera at the splat's centre
        //     of mass, target 3 units forward along +X.
        const fallback: CameraPose = {
          position: [com.x, com.y, com.z],
          target: [com.x + 3, com.y, com.z],
        };
        const startPose: CameraPose =
          savedPoseRef.current ?? FALLBACK_POSE ?? fallback;

        const applyPose = (pose: CameraPose) => {
          if (viewer.camera) {
            viewer.camera.position.set(...pose.position);
            viewer.camera.up.set(0, 0, 1);
            viewer.camera.lookAt(...pose.target);
            viewer.camera.near = Math.max(0.02, radius / 1000);
            viewer.camera.far = Math.max(2000, radius * 20);
            viewer.camera.updateProjectionMatrix();
          }
          if (viewer.controls) {
            viewer.controls.target.set(...pose.target);
            viewer.controls.minDistance = 0.05;
            viewer.controls.maxDistance = radius * 4;
            viewer.controls.update?.();
          }
        };
        applyPose(startPose);
        setReady(true);

        handlesRef.current = {
          reset: () => {
            // Always pick up the latest saved pose (which may have
            // changed via onSnapshot since mount) before falling
            // back to the source-baked default.
            const pose = savedPoseRef.current ?? FALLBACK_POSE ?? fallback;
            applyPose(pose);
          },
          snapshot: () => {
            try {
              const p = viewer.camera?.position;
              const t = viewer.controls?.target;
              if (!p || !t) return null;
              return {
                position: [p.x, p.y, p.z] as [number, number, number],
                target: [t.x, t.y, t.z] as [number, number, number],
              };
            } catch {
              return null;
            }
          },
          applyPose,
        };

        cleanup = () => {
          // The lib's dispose() is async and tears down its own DOM
          // children. If we synchronously empty the container right
          // after kicking it off, the async branch tries to
          // removeChild() a node that's already gone and throws an
          // unhandled NotFoundError. Chain the cleanup into the
          // dispose promise instead, swallow rejections, and only
          // belt-and-brace the container clear once dispose has
          // settled — wrapping each removeChild so a parent that
          // dispose already detached doesn't fail the whole loop.
          try {
            viewer.stop?.();
          } catch {
            /* stop is best-effort; dispose handles the rest */
          }
          Promise.resolve()
            .then(() => viewer.dispose?.())
            .catch(() => {
              /* lib raced with our unmount; safe to ignore */
            })
            .finally(() => {
              while (container.firstChild) {
                try {
                  container.removeChild(container.firstChild);
                } catch {
                  break;
                }
              }
            });
        };
      } catch (err) {
        if (!disposed) {
          console.error('[FieldSplatViewer] failed to render:', err);
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to load Gaussian Splat.',
          );
        }
      }
    })();

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [splatSrc]);

  // ── Fullscreen toggle ───────────────────────────────────────────────
  const toggleFullscreen = () => {
    const el = containerRef.current?.parentElement;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.()
        .then(() => setFullscreen(true))
        .catch(() => undefined);
    } else {
      document.exitFullscreen?.()
        .then(() => setFullscreen(false))
        .catch(() => undefined);
    }
  };

  React.useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  return (
    <div className="relative w-full h-[70vh] min-h-[420px] rounded-xl overflow-hidden bg-zinc-950 ring-1 ring-zinc-800">
      <div ref={containerRef} className="absolute inset-0" />

      {/* Floating overlay controls */}
      <div className="absolute top-3 right-3 flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="bg-zinc-900/80 backdrop-blur border border-zinc-700 text-zinc-100 hover:bg-zinc-800"
          onClick={async () => {
            const snap = handlesRef.current?.snapshot();
            if (!snap) return;
            const round = (n: number) => Math.round(n * 1000) / 1000;
            const text =
              `position: [${round(snap.position[0])}, ${round(snap.position[1])}, ${round(snap.position[2])}],\n` +
              `target:   [${round(snap.target[0])}, ${round(snap.target[1])}, ${round(snap.target[2])}],`;
            try {
              await navigator.clipboard.writeText(text);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {
              // Fallback: dump to console so the user can grab it from devtools.
              console.log('[field-splat-viewer] camera snapshot:\n' + text);
            }
          }}
          disabled={!ready}
          title="Copy current camera position + target to clipboard"
        >
          {copied ? (
            <Check className="h-4 w-4 mr-1.5 text-emerald-400" />
          ) : (
            <Copy className="h-4 w-4 mr-1.5" />
          )}
          {copied ? 'Copied!' : 'Copy camera'}
        </Button>
        {canSetDefault && (
          <Button
            type="button"
            size="sm"
            className="bg-red-600 hover:bg-red-500 text-white"
            onClick={async () => {
              const snap = handlesRef.current?.snapshot();
              if (!snap || !firestore) return;
              setSavingDefault(true);
              try {
                await setDoc(
                  doc(firestore, 'siteConfig', 'fieldMap'),
                  {
                    position: snap.position,
                    target: snap.target,
                    updatedAt: serverTimestamp(),
                  },
                  { merge: true },
                );
                toast({
                  title: 'Saved as default',
                  description:
                    'Visitors will now land here on first paint. Refresh to verify.',
                });
              } catch (err) {
                console.error('[FieldSplatViewer] save default failed:', err);
                toast({
                  variant: 'destructive',
                  title: 'Could not save',
                  description:
                    err instanceof Error ? err.message : 'Unknown error.',
                });
              } finally {
                setSavingDefault(false);
              }
            }}
            disabled={!ready || savingDefault}
            title="Save the current viewpoint as the default for everyone"
          >
            {savingDefault ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Pin className="h-4 w-4 mr-1.5" />
            )}
            Save as default
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="bg-zinc-900/80 backdrop-blur border border-zinc-700 text-zinc-100 hover:bg-zinc-800"
          onClick={() => handlesRef.current?.reset()}
          disabled={!ready}
          title="Drop me back at the saved default viewpoint"
        >
          <RotateCcw className="h-4 w-4 mr-1.5" />
          Reset
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="bg-zinc-900/80 backdrop-blur border border-zinc-700 text-zinc-100 hover:bg-zinc-800"
          onClick={toggleFullscreen}
          title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {fullscreen ? (
            <Minimize2 className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Help hint pinned bottom-left */}
      {ready && (
        <div className="absolute bottom-3 left-3 text-[11px] text-zinc-300 bg-black/60 backdrop-blur rounded px-2 py-1 pointer-events-none">
          Drag to look around · Scroll to step forward / back · Shift-drag to
          pan
        </div>
      )}

      {/* Loading overlay — covers the full lifecycle (download +
          sort) so we can keep the lib's own loading UI suppressed
          and only ever show this single red bar. */}
      {!ready && !error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-zinc-200 pointer-events-none">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
          <div className="text-sm font-medium">{progressLabel}</div>
          <div className="w-72 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
            <div
              className="h-full bg-red-600 transition-[width]"
              style={{ width: `${Math.max(4, progress)}%` }}
            />
          </div>
          <div className="text-[11px] text-zinc-400">
            {progress > 0 ? `${Math.round(progress)}%` : ''}
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-zinc-200 px-6 text-center">
          <AlertTriangle className="h-8 w-8 text-amber-400" />
          <div className="text-sm font-semibold">3D scene unavailable</div>
          <div className="text-xs text-zinc-400 max-w-sm">{error}</div>
        </div>
      )}
    </div>
  );
}
